import { useEnvironmentRuntimeSettings } from "@@/settings/app-settings/environment-settings-context";
import {
    useStorageItemImageReferenceQueries,
    useStorageItemImageReferenceQuery,
} from "@@/storage-items/queries/use-storage-item-reference";
import { useTheme } from "@emotion/react";
import type { ImageRef, ImageUrl, ImageUrlCreatorOptions } from "@towni/common";
import {
    exists,
    getImageUrlFactory,
    isStorageItemImageId,
} from "@towni/common";
import { useCallback, useMemo } from "react";

const useGetImageUrl = () => {
    const { MEDIA_ENDPOINT } = useEnvironmentRuntimeSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const result = useCallback(getImageUrlFactory(MEDIA_ENDPOINT), [
        MEDIA_ENDPOINT,
    ]);
    return result;
};

const useImageUrl = (
    imageSource: ImageRef | undefined,
    options: ImageUrlCreatorOptions & {
        imageWidth: NonNullable<ImageUrlCreatorOptions["imageWidth"]>;
    },
): ImageUrl => {
    const theme = useTheme();
    const { MEDIA_ENDPOINT } = useEnvironmentRuntimeSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getImageUrl = useCallback(getImageUrlFactory(MEDIA_ENDPOINT), [
        MEDIA_ENDPOINT,
    ]);

    const imageSourceNeedsLoading = isStorageItemImageId(imageSource);
    const storageItemImageQuery = useStorageItemImageReferenceQuery(
        imageSourceNeedsLoading ? imageSource : undefined,
    );
    const storageItemImage = storageItemImageQuery.data;
    const _imageSource = imageSourceNeedsLoading
        ? storageItemImage
        : imageSource;

    // We'll ask imgix for auto negotiated format
    // which will return avif/heif or webp for most
    // browsers/customers, and will fallback to png
    // to allow for transparency in images that doesn't
    // support avif/heif/webp and fails to negotiate
    const format = "png";
    const { imageWidth, ...passOnOptions } = options;
    const closestWidth =
        imageWidth === "full"
            ? "full"
            : theme.sizes.imageSizes.closest(imageWidth);

    const url = getImageUrl(_imageSource, {
        format,
        ...passOnOptions,
        imageWidth: closestWidth,
    });

    return url as ImageUrl;
};

const useGetImageUrlsFromImageRefs = (
    imageSources: ImageRef[],
    options: ImageUrlCreatorOptions,
): {
    /** The index corresponding to the imageSources index */
    index: number;
    /** The image url */
    url: string | undefined;
}[] => {
    const getImageUrl = useGetImageUrl();
    const storageItemImageIds = useMemo(
        () => imageSources.filter(imgSrc => isStorageItemImageId(imgSrc)),
        [imageSources],
    );
    const storageItemImageQuery =
        useStorageItemImageReferenceQueries(storageItemImageIds);
    const storageItemImages = storageItemImageQuery
        .map(query => query.data)
        .filter(exists);
    const urls = useMemo(() => {
        const result = imageSources.map((imgSrc, index) => {
            storageItemImages.forEach(img => img._id);
            if (isStorageItemImageId(imgSrc)) {
                const storageItemImage = storageItemImages.find(
                    img => img._id === imgSrc,
                );
                return {
                    index,
                    url: getImageUrl(storageItemImage, options),
                };
            }
            return {
                index,
                url: getImageUrl(imgSrc, options),
            };
        });
        return result.filter(exists);
    }, [imageSources, storageItemImages, getImageUrl, options]);

    return urls;
};

export { useGetImageUrl, useGetImageUrlsFromImageRefs, useImageUrl };
